import { useState, useEffect } from 'react';

/**
 * Custom hook to detect virtual keyboard visibility on mobile devices
 * @returns {boolean} whether the keyboard is visible or not
 */
export const useKeyboardVisibility = () => {
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  useEffect(() => {
    // Initial window height and screen orientation
    let windowHeight = window.innerHeight;
    let lastOrientation = window.orientation;

    const handleResize = () => {
      // Skip if orientation changed to avoid false positives
      if (lastOrientation !== window.orientation) {
        lastOrientation = window.orientation;
        windowHeight = window.innerHeight;
        return;
      }

      // If window height becomes significantly smaller (>150px difference)
      // we can assume keyboard is visible
      const keyboardVisible = window.innerHeight < windowHeight - 150;
      setIsKeyboardVisible(keyboardVisible);
    };

    // Visual viewport API provides more reliable keyboard detection when available
    if (window.visualViewport) {
      const handleVisualViewportResize = () => {
        // If viewport height is significantly smaller than window height
        const keyboardVisible = window.visualViewport.height < windowHeight - 150;
        setIsKeyboardVisible(keyboardVisible);
      };
      
      window.visualViewport.addEventListener('resize', handleVisualViewportResize);
      return () => {
        window.visualViewport.removeEventListener('resize', handleVisualViewportResize);
      };
    } else {
      // Fallback to window resize method
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  return isKeyboardVisible;
};
